import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';

import { UserModule } from 'user/user-module';
import { UserModuleAnswersGeneratedLinkedinProposition } from 'user/user-module-answers-generated-linkedin-proposition';
import { UserModulePage } from 'user/user-module-page';

export interface UserModulePageDocument {
	readonly pageId: string;
	readonly activityUrl: string;
}

export interface UserModuleDocument {
	readonly id: string;
	readonly userId: string;
	readonly isDone: boolean;
	readonly moduleId: string;
	readonly isStarted: boolean;
	readonly coachComment: string;
	readonly moduleRatingValue: number;
	readonly moduleRatingComment: string;
	readonly updateTimestamp: number;
	readonly pages: UserModulePageDocument[];
	readonly isDescriptionVisualized: boolean;
	readonly generatedUserModuleSynthesis: string;
	readonly generatedUserModulePitch: string;
	readonly generatedUserModulePushSynthesis: string;
	readonly generatedUserModulePushQuestionsSynthesis: { [key: string]: string };
	readonly generatedUserModuleLinkedinProposition: UserModuleAnswersGeneratedLinkedinProposition[];
}

export type UserModuleState = EntityState<UserModuleDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'userModules' })
export class UserModuleStore extends EntityStore<UserModuleState> {
	constructor() {
		super();
	}

	public asDocument(userModule: UserModule): UserModuleDocument {
		return userModule
			? {
					pages: userModule.pages.map((page) => ({
						activityUrl: page.activityUrl,
						pageId: page.pageId,
					})),
					updateTimestamp: userModule.updateTimestamp,
					id: userModule.id,
					userId: userModule.userId,
					isDone: userModule.isDone,
					moduleId: userModule.moduleId,
					isStarted: userModule.isStarted,
					coachComment: userModule.coachComment,
					moduleRatingValue: userModule.moduleRatingValue,
					moduleRatingComment: userModule.moduleRatingComment,
					isDescriptionVisualized: userModule.isDescriptionVisualized,
					generatedUserModuleSynthesis: userModule.generatedUserModuleSynthesis,
					generatedUserModulePitch: userModule.generatedUserModulePitch,
					generatedUserModulePushSynthesis:
						userModule.generatedUserModulePushSynthesis,
					generatedUserModulePushQuestionsSynthesis:
						userModule.generatedUserModulePushQuestionsSynthesis,
					generatedUserModuleLinkedinProposition:
						userModule.generatedUserModuleLinkedinProposition,
			  }
			: null;
	}

	public asEntity(document: UserModuleDocument): UserModule {
		return document
			? new UserModule(
					document.userId,
					document.isDone,
					document.moduleId,
					document.isStarted,
					document.coachComment,
					document.moduleRatingComment,
					document.moduleRatingValue,
					(document.pages || []).map(
						(page) => new UserModulePage(page.pageId, page.activityUrl)
					),
					document.updateTimestamp,
					document.isDescriptionVisualized,
					document.generatedUserModuleSynthesis,
					document.generatedUserModulePitch,
					document.generatedUserModulePushSynthesis,
					document.generatedUserModulePushQuestionsSynthesis,
					document.generatedUserModuleLinkedinProposition
			  )
			: null;
	}
}
